import React from 'react';
import { Button } from '@roger/ui-components';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { formatDateTimeInAgencyZone } from '../../../utils/dateUtils';
import type {
  CahpsBranchSummary,
  CahpsExportListItem,
  CahpsGenerateSummary,
} from '../../../services/cahpsExports';

function branchProblem(branch: CahpsBranchSummary): string | null {
  const name = `Branch ${branch.branch_name}`;
  switch (branch.status) {
    case 'not_set_up':
      // Only a problem when the branch belongs to an SHP provider number.
      return branch.shp_provider_no
        ? `${name} is not set up for CAHPS in your previous EMR.`
        : null;
    case 'unsupported_vendor':
      return `${name} uses an unsupported survey administrator.`;
    case 'unreadable_file':
      return `The CAHPS file for branch ${branch.branch_name} could not be used.`;
    case 'no_roger_clinic':
      return `${name} is not linked to a Roger clinic with an SHP number.`;
    case 'failed':
      return branch.shp_provider_no
        ? `${name} could not be read from your previous EMR.`
        : `${name} could not be read from your previous EMR and is not linked to a Roger clinic, so its SHP number is unknown.`;
    default:
      return null;
  }
}

const BOTH = 'Roger and previous EMR patients.';
const ROGER_ONLY = 'Roger patients only.';

interface ProviderLine {
  // Absent when the line is about rows no SHP number can be found for.
  provider?: string;
  complete: boolean;
  text: string;
}

// What each SHP number of an incomplete file holds, and why any is short.
function providerLines(summary: CahpsGenerateSummary): ProviderLine[] {
  if (summary.kinnser?.source === 'failed') {
    return [
      {
        complete: false,
        text: `${ROGER_ONLY} ${summary.kinnser.failure ?? ''}`.trim(),
      },
    ];
  }
  const branches = summary.branches ?? [];
  // Only when nothing was set up; a branch that failed explains itself below.
  const noSetup = branches.every(branch => branch.status === 'not_set_up')
    ? 'No branch is set up for CAHPS in your previous EMR.'
    : null;
  const lines: ProviderLine[] = (summary.providers ?? []).map(provider => {
    const hasKinnser = (provider.kinnser_rows ?? 0) > 0;
    if (provider.complete !== false) {
      return {
        provider: provider.shp_provider_no,
        complete: true,
        text: hasKinnser ? BOTH : ROGER_ONLY,
      };
    }
    const causes = branches
      .filter(branch => branch.shp_provider_no === provider.shp_provider_no)
      .map(branchProblem)
      .filter((text): text is string => text !== null);
    const lead = hasKinnser
      ? 'Some previous EMR patients are missing.'
      : ROGER_ONLY;
    return {
      provider: provider.shp_provider_no,
      complete: false,
      text: [
        lead,
        ...(causes.length > 0 || !noSetup ? causes : [noSetup]),
      ].join(' '),
    };
  });
  const unplaced = branches
    .filter(branch => !branch.shp_provider_no)
    .map(branchProblem)
    .filter((text): text is string => text !== null);
  if (unplaced.length > 0) {
    lines.push({
      complete: false,
      text: `Previous EMR patients are missing. ${unplaced.join(' ')}`,
    });
  }
  if (lines.length > 0) {
    return lines;
  }
  return [
    {
      complete: false,
      text: noSetup
        ? `${ROGER_ONLY} ${noSetup}`
        : 'Some previous EMR patients may be missing.',
    },
  ];
}

// Only an agency moving from another EMR gets a line; others see just Complete.
function completeLine(summary: CahpsGenerateSummary): string | null {
  const source = summary.kinnser?.source;
  return source === 'read' || source === 'stored' ? BOTH : null;
}

export function IncompleteMark({
  complete,
}: {
  complete: boolean | null | undefined;
}) {
  if (complete !== false) {
    return null;
  }
  return (
    <span className='whitespace-nowrap text-xs font-medium text-amber-600'>
      Incomplete
    </span>
  );
}

interface CahpsSummaryPanelProps {
  item: CahpsExportListItem;
  periodLabel: string;
  isDownloading: boolean;
  onDownload: () => void;
}

const CahpsSummaryPanel: React.FC<CahpsSummaryPanelProps> = ({
  item,
  periodLabel,
  isDownloading,
  onDownload,
}) => {
  const summary = item.summary;
  const complete = summary ? completeLine(summary) : null;
  const generated = `Generated ${formatDateTimeInAgencyZone(item.created_at)}${
    item.generated_by ? ` by ${item.generated_by}` : ''
  } · ${item.row_count} patients`;

  return (
    <section aria-label='Generate summary' className='space-y-4'>
      <div>
        <div className='flex items-center justify-between gap-3'>
          <h3 className='flex items-center gap-2 text-base font-semibold'>
            <FileSpreadsheet className='h-5 w-5 shrink-0 text-muted-foreground' />
            {periodLabel}
          </h3>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={isDownloading}
            onClick={onDownload}
          >
            {isDownloading ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Download className='mr-2 h-4 w-4' />
            )}
            Download
          </Button>
        </div>
        <p className='mt-1 text-sm text-muted-foreground'>{generated}</p>
      </div>

      {!summary ? (
        <p className='text-sm text-muted-foreground'>
          No summary was saved for this export.
        </p>
      ) : (
        <div className='space-y-1 text-sm'>
          {summary.complete === false ? (
            <>
              <p className='font-medium text-amber-600'>Incomplete</p>
              <ul className='space-y-3 pt-2'>
                {providerLines(summary).map(line => (
                  <li key={`${line.provider ?? ''}-${line.text}`}>
                    {line.provider && (
                      <p className='font-semibold'>
                        SHP {line.provider}{' '}
                        <span
                          className={
                            line.complete
                              ? 'font-medium text-muted-foreground'
                              : 'font-medium text-amber-600'
                          }
                        >
                          · {line.complete ? 'Complete' : 'Incomplete'}
                        </span>
                      </p>
                    )}
                    <p className='text-muted-foreground'>{line.text}</p>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <p className='font-medium'>Complete</p>
              {complete && <p className='text-muted-foreground'>{complete}</p>}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default CahpsSummaryPanel;
